'use client';

import { Box, Text, Card, Flex, Checkbox } from '@radix-ui/themes';
import { useForm } from 'react-hook-form';
import {
  trpcConsumerSubmitServiceDate,
  trpcConsumerListPricing,
  trpcConsumerGetCalendarDays,
} from '@trashlab/api/src/generated/hooks';
import {
  buildCalendarForecastInput,
  buildSitesCalendarForecastRequirement,
} from '@trashlab/sitesapp/src/components/shop/calendar-forecast-input';
import {
  TLSiteCartData,
  TLSiteShopStep,
} from '@trashlab/api/src/domains/consumer/types';
import { SummarySectionCard } from '@trashlab/sitesapp/src/components/shop/summary-section-card';
import {
  IconShopDelivery,
  IconShopPickup,
} from '@trashlab/sitesapp/src/templates/default/components/icons/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { ShopStepFrame } from '@trashlab/sitesapp/src/components/shop/shop-step-frame';
import { useState, useEffect, useCallback, useRef } from 'react';
import { DateTime } from 'luxon';
import ShopCalendar from '@trashlab/sitesapp/src/components/shop/shop-calendar';
import validations from '@trashlab/api/src/domains/consumer/validations/index';
import currency from 'currency.js';

export const SelectDatesStepSummary = ({
  cartData,
}: {
  cartData: TLSiteCartData;
}) => {
  if (!cartData.cart.deliveryServiceDate) {
    return null;
  }

  return (
    <>
      <SummarySectionCard icon={IconShopDelivery} label="Delivery date">
        <Text>
          {cartData.cart.deliveryServiceDate
            ? DateTime.fromISO(
                cartData.cart.deliveryServiceDate,
              ).toLocaleString(DateTime.DATE_MED)
            : '-'}
        </Text>
      </SummarySectionCard>

      {cartData.cart.pickupServiceDate && (
        <SummarySectionCard icon={IconShopPickup} label="Pickup date">
          <Text>
            {DateTime.fromISO(cartData.cart.pickupServiceDate).toLocaleString(
              DateTime.DATE_MED,
            )}
          </Text>
        </SummarySectionCard>
      )}
    </>
  );
};

export const SelectDatesStep = ({ cartData }: { cartData: TLSiteCartData }) => {
  const listPricingQuery = trpcConsumerListPricing.useQuery({
    cartId: cartData.cart.id,
    tenantId: cartData.cart.tenantId,
  });

  const submitMutation = trpcConsumerSubmitServiceDate.useMutation();

  const [excludePickupDate, setExcludePickupDate] = useState(
    (!cartData.cart.shop?.requirePickupDate &&
      cartData.cart.deliveryServiceDate &&
      !cartData.cart.pickupServiceDate) ||
      false,
  );

  const form = useForm({
    defaultValues: {
      deliveryServiceDate: cartData.cart.deliveryServiceDate ?? '',
      pickupServiceDate: cartData.cart.pickupServiceDate ?? '',
      daysOver: cartData.cart.daysOver ?? 0,
      cartId: cartData.cart.id,
      isWebsite: true,
    },
    resolver: zodResolver(validations.submitServiceDate),
  });

  const [isFormValid, setIsFormValid] = useState(false);

  // Store watched values in stable variables to prevent infinite loops
  const watchedDeliveryServiceDate = form.watch('deliveryServiceDate');
  const watchedPickupServiceDate = form.watch('pickupServiceDate');
  const watchedDaysOver = form.watch('daysOver');

  const daysOverRef = useRef(watchedDaysOver);

  useEffect(() => {
    daysOverRef.current = watchedDaysOver;
  }, [watchedDaysOver]);

  const handleDaysOverChange = useCallback(
    (daysOver: number) => {
      if (daysOverRef.current !== daysOver) {
        daysOverRef.current = daysOver;
        form.setValue('daysOver', daysOver);
      }
    },
    [form],
  );

  const priceCard = listPricingQuery.data?.priceCards.find(
    (pc) => pc.id === cartData.cart.skuId,
  );

  const overageCost =
    watchedDaysOver * (priceCard?.additionalDaysPrice?.chargeFee ?? 0);

  const deliveryDate = watchedDeliveryServiceDate
    ? DateTime.fromISO(watchedDeliveryServiceDate).toJSDate()
    : null;

  const pickupDate =
    watchedPickupServiceDate && !excludePickupDate
      ? DateTime.fromISO(watchedPickupServiceDate).toJSDate()
      : null;

  const calendarForecastRequirement =
    buildSitesCalendarForecastRequirement({
      productId: cartData.cart.productId ?? undefined,
      blockDatesWithoutInventory:
        cartData.cart.shop?.blockDatesWithoutInventory ?? false,
      showForecastOnHover: false,
    });

  const calendarDaysQuery = trpcConsumerGetCalendarDays.useQuery(
    buildCalendarForecastInput({
      tenantId: cartData.cart.tenantId,
      ...calendarForecastRequirement,
      startRentalFromDeliveryDate:
        priceCard?.startRentalFromDeliveryDate ?? false,
      blockSameDayOrder: cartData.cart.shop?.blockSameDayOrder ?? false,
      countInactiveDaysAsRentalDays:
        cartData.cart.shop?.countInactiveDaysAsRentalDays ?? false,
      daysLimit: priceCard?.daysLimit || undefined,
      selectedRange: [
        watchedDeliveryServiceDate || null,
        watchedPickupServiceDate && !excludePickupDate
          ? watchedPickupServiceDate
          : null,
      ],
      hoveredDate: null,
    }),
    {
      enabled:
        !!watchedDeliveryServiceDate &&
        (!!watchedPickupServiceDate || excludePickupDate),
      staleTime: 60000,
    },
  );

  useEffect(() => {
    const checkValidity = () => {
      if (watchedDeliveryServiceDate && excludePickupDate) {
        setIsFormValid(true);
        return;
      }

      if (watchedDeliveryServiceDate && watchedPickupServiceDate) {
        setIsFormValid(true);
        return;
      }

      setIsFormValid(false);
    };

    checkValidity();
    form.trigger();
  }, [
    watchedDeliveryServiceDate,
    watchedPickupServiceDate,
    excludePickupDate,
    form,
  ]);

  useEffect(() => {
    if (calendarDaysQuery.data?.daysOver !== undefined) {
      form.setValue('daysOver', calendarDaysQuery.data.daysOver);
    }
  }, [calendarDaysQuery.data?.daysOver, form]);

  const canProceed =
    isFormValid && (!calendarDaysQuery.isLoading || excludePickupDate);

  return (
    <ShopStepFrame
      cartId={cartData.cart.id}
      onNext={async () => {
        await submitMutation.mutateAsync({
          ...form.getValues(),
          tenantId: cartData.cart.tenantId,
          pickupServiceDate: excludePickupDate
            ? null
            : form.getValues().pickupServiceDate,
          isWebsite: true,
        });
      }}
      canContinue={canProceed}
      isLoading={submitMutation.isLoading || calendarDaysQuery.isLoading}
    >
      <Flex direction="column" gap="2" my="2">
        <Text size="4" weight="bold">
          Select your delivery and pickup day
        </Text>

        <Card className="mb-4 shadow-md">
          <ShopCalendar
            tenantId={cartData.cart.tenantId}
            daysLimit={priceCard?.daysLimit ?? undefined}
            startRentalFromDeliveryDate={
              priceCard?.startRentalFromDeliveryDate ?? false
            }
            pickupDateOptional={excludePickupDate}
            forecastRequirement={calendarForecastRequirement}
            blockSameDayOrder={cartData.cart.shop?.blockSameDayOrder ?? false}
            sameDayOrderCutoffTime={
              cartData.cart.shop?.sameDayOrderCutoffTime || undefined
            }
            blockedDaysFromToday={cartData.cart.shop?.blockedDaysFromToday ?? 0}
            countInactiveDaysAsRentalDays={
              cartData.cart.shop?.countInactiveDaysAsRentalDays ?? false
            }
            onDaysOverChange={handleDaysOverChange}
            value={[deliveryDate, pickupDate]}
            onChange={(dates) => {
              form.setValue(
                'deliveryServiceDate',
                dates[0] ? DateTime.fromJSDate(dates[0]).toISODate() ?? '' : '',
              );

              form.setValue(
                'pickupServiceDate',
                dates[1] ? DateTime.fromJSDate(dates[1]).toISODate() ?? '' : '',
              );

              form.trigger();
            }}
          />
        </Card>

        {!cartData.cart.shop?.requirePickupDate && (
          <Flex as="span" align="center" gap="2" mb="2">
            <Checkbox
              size="3"
              id="exclude-pickup-date"
              name="excludePickupDate"
              checked={excludePickupDate}
              onCheckedChange={(checked) => {
                setExcludePickupDate(checked === true);
                if (checked === true) {
                  form.setValue('pickupServiceDate', '');
                }
              }}
            />
            <Text>I don't know my pickup date yet</Text>
          </Flex>
        )}

        {watchedDaysOver > 0 && !excludePickupDate && (
          <Box className="p-2 bg-amber-50 border border-amber-200 rounded-md">
            <Text size="2" color="amber">
              {watchedDaysOver} {watchedDaysOver === 1 ? 'day' : 'days'} over (
              {priceCard?.additionalDaysPrice?.chargeFee
                ? currency(priceCard.additionalDaysPrice.chargeFee, {
                    fromCents: true,
                  }).format()
                : '$0.00'}
              /day over) {currency(overageCost, { fromCents: true }).format()}
            </Text>
          </Box>
        )}
      </Flex>
    </ShopStepFrame>
  );
};
